import React, { useEffect, useState } from 'react'
import {
  Navigate,
  useParams,
  Route,
  useOutletContext,
  Routes,
  useSearchParams
} from 'react-router-dom'
import Loading from '@renderer/components/Loading'
import NotFound from '@renderer/pages/NotFound'
import ViewProject from '@renderer/pages/View'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { useEnforceProjectLanguage } from '@renderer/hooks'
import FailedToInitialize from '@renderer/pages/FailedToInitialize'
import {
  getStoredProjectEffectiveUpdatedAt,
  setStoredProjectEffectiveUpdatedAt
} from '@renderer/utils/projectVersionCache'
import { isDigitalMode } from '@renderer/lib/runtime'
import { applyDigitalProjection } from '@renderer/lib/digitalProjection'
import { resolveDigitalProjectId } from '@renderer/lib/digitalProject'
import SimHost, { SimFlowRecapture } from '@renderer/pages/sim/SimHost'
import { cacheProjectConfig, readCachedProject } from '@renderer/utils/projectConfigCache'

interface User {
  _id: string
  email: string
  username: string
}

/**
 * Resolve the route param (`:projectId`) to a concrete project id.
 *
 * Kiosk mode: the param IS the id — return it unchanged.
 * Digital mode: the param is a slug (or, for superadmin QA, a raw ObjectId);
 *   translate it via the public, `isPublic`-gated studio endpoint. See
 *   {@link resolveDigitalProjectId}.
 *
 * Returns `null` when the param doesn't resolve to a public, active project.
 */
async function resolveProjectId(paramValue: string): Promise<string | null> {
  if (!isDigitalMode()) return paramValue
  return resolveDigitalProjectId(paramValue)
}

const ProjectRoute: React.FC = () => {
  const { projectId: routeParam } = useParams()
  const [searchParams] = useSearchParams()
  const eventId = searchParams.get('eventId') || undefined
  const [isLoading, setIsLoading] = useState(true)
  const [isValidProject, setIsValidProject] = useState<boolean | null>(null)
  const [isInitialized, setIsInitialized] = useState<boolean | null>(null)
  const { user } = useOutletContext<{ user: User }>()
  const { setConfig } = useConfigStore()
  const { initializeSession, refreshProjectCatalog } = useSessionStateStore()
  // Non-IDR projects force `i18n.language = 'en'` regardless of the
  // operator's persisted preference. Doesn't write to localStorage, so
  // leaving the project (or opening an IDR one next) reverts to
  // whatever the operator had picked globally.
  useEnforceProjectLanguage()

  useEffect(() => {
    const validateAndInitProject = async (): Promise<void> => {
      if (!routeParam || !user) {
        setIsValidProject(false)
        setIsLoading(false)
        return
      }

      // In digital mode, translate slug → id once. In kiosk mode this is
      // a pass-through.
      const projectId = await resolveProjectId(routeParam)
      if (!projectId) {
        setIsValidProject(false)
        setIsLoading(false)
        return
      }

      const configSnapshot = useConfigStore.getState().config

      // Offline fallback (multi-project hub, plan §4.4). This bootstrap used to
      // run once per boot, so an offline booth never noticed it needs the
      // network; a hub runs it on every hub → member → hub hop. When the
      // version fetch cannot reach Studio (thrown, or a 5xx — never a 4xx,
      // which is a real answer) and the cache holds this project, replay the
      // cached config and let the catalogue loader fall back the same way.
      const bootFromCache = async (reason: unknown): Promise<boolean> => {
        const cached = readCachedProject(projectId)
        if (!cached?.config || cached.config._id !== projectId) return false
        console.warn(`[PROJECT-ROUTE] offline_config_used for ${projectId}:`, reason)
        try {
          setConfig(isDigitalMode() ? applyDigitalProjection(cached.config) : cached.config)
          if (cached.effectiveUpdatedAt) {
            setStoredProjectEffectiveUpdatedAt(projectId, cached.effectiveUpdatedAt)
          }
          setIsValidProject(true)
          await initializeSession({ projectId, user, eventId })
          setIsInitialized(true)
        } catch (error) {
          console.error('Error initializing session from cache:', error)
          setIsInitialized(false)
        } finally {
          setIsLoading(false)
        }
        return true
      }

      try {
        let versionResponse: Response
        try {
          versionResponse = await fetch(
            `${process.env.VITE_API_URL}/api/projects/version?id=${projectId}`,
            {
              credentials: 'include',
              cache: 'no-cache'
            }
          )
        } catch (networkError) {
          if (await bootFromCache(networkError)) return
          throw networkError
        }

        if (!versionResponse.ok) {
          if (versionResponse.status >= 500 && (await bootFromCache(versionResponse.status))) {
            return
          }
          setIsValidProject(false)
          setIsLoading(false)
          return
        }

        const versionPayload = (await versionResponse.json()) as {
          effectiveUpdatedAt: string
        }
        const { effectiveUpdatedAt } = versionPayload

        const storedVersion = getStoredProjectEffectiveUpdatedAt(projectId)
        const configMatches =
          Boolean(
            configSnapshot &&
              '_id' in configSnapshot &&
              configSnapshot._id === projectId
          )
        const versionMatches =
          storedVersion !== undefined && storedVersion === effectiveUpdatedAt

        if (versionMatches && configMatches) {
          setStoredProjectEffectiveUpdatedAt(projectId, effectiveUpdatedAt)
          setIsValidProject(true)
          try {
            await initializeSession({ projectId, user, eventId })
            setIsInitialized(true)
          } catch (error) {
            console.error('Error initializing session:', error)
            setIsInitialized(false)
          } finally {
            setIsLoading(false)
          }
          return
        }

        // Prefetched hub member (or any project the cache saw at this exact
        // version): the server just told us nothing changed, so the cached
        // config IS the current one — skip the download and make the pick →
        // first page hop instant. The catalogue is still fetched below.
        const cachedAtVersion = readCachedProject(projectId)
        const cacheHit =
          cachedAtVersion?.config &&
          cachedAtVersion.config._id === projectId &&
          cachedAtVersion.effectiveUpdatedAt === effectiveUpdatedAt
            ? cachedAtVersion.config
            : null

        let projectData = cacheHit
        if (!projectData) {
          const projectResponse = await fetch(
            `${process.env.VITE_API_URL}/api/projects?id=${projectId}`,
            {
              credentials: 'include',
              cache: 'no-cache'
            }
          )

          if (!projectResponse.ok) {
            setIsValidProject(false)
            setIsLoading(false)
            return
          }

          projectData = await projectResponse.json()
        }
        if (!projectData || projectData._id !== projectId) {
          setIsValidProject(false)
          setIsLoading(false)
          return
        }
        setIsValidProject(true)

        const sessionHadSameProjectBefore =
          useSessionStateStore.getState().projectId === projectId

        try {
          // In digital mode, strip kiosk-only pages/settings server-side
          // of the renderer (i.e. before the config hits the store). The
          // input from the studio API is identical in shape; only the
          // runtime values change. Kiosk path is a pure pass-through.
          const configForRenderer = isDigitalMode()
            ? applyDigitalProjection(projectData)
            : projectData
          setConfig(configForRenderer)
          // Raw server config (pre-projection) so an offline replay goes
          // through the same digital projection as a live load.
          if (!cacheHit) cacheProjectConfig(projectId, effectiveUpdatedAt, projectData)
          // Booth pages carry the booth's name in the tab title — works for
          // both the slug path (resolved above) and the raw ObjectId path.
          if (typeof projectData.title === 'string' && projectData.title.trim()) {
            document.title = `${projectData.title.trim()} · Dreambooth`
          }
          setStoredProjectEffectiveUpdatedAt(projectId, effectiveUpdatedAt)
          await initializeSession({ projectId, user, eventId })
          if (sessionHadSameProjectBefore) {
            await refreshProjectCatalog(projectId)
          }
          setIsInitialized(true)
        } catch (error) {
          console.error('Error initializing session:', error)
          setIsInitialized(false)
        }
      } catch (error) {
        console.error('Error validating project:', error)
        setIsValidProject(false)
      } finally {
        setIsLoading(false)
      }
    }

    void validateAndInitProject()
    // Intentionally exclude `config` from deps: updates to config from welcome refresh must not re-run bootstrap.
  }, [routeParam, user, eventId, initializeSession, refreshProjectCatalog, setConfig])

  // Sim Mode deep-load recapture. A HARD load of /:projectId/sim cannot boot the
  // app (web build uses base:'./', so nested paths break module resolution); the
  // index.html normalizer bounces it to /:projectId?sim=1&<params>. Here — after
  // the app has booted at the safe one-segment URL — we complete the round-trip
  // with a CLIENT-side navigation back to /:projectId/sim (no document reload, so
  // relative assets are untouched). Kiosk only: in digital mode the sim route is
  // not registered, so the marker is deliberately ignored.
  const simRedirect = searchParams.get('sim') === '1' && !isDigitalMode() && routeParam
  if (simRedirect) {
    const params = new URLSearchParams(searchParams)
    params.delete('sim')
    const qs = params.toString()
    // Absolute target: relative `to` resolution inside splat routes varies across
    // react-router minors — don't gamble the entry point on it twice.
    return <Navigate to={`/${routeParam}/sim${qs ? `?${qs}` : ''}`} replace />
  }

  if (isLoading) {
    return <Loading />
  }

  if (!isValidProject) {
    return <NotFound />
  }

  if (isInitialized === false) {
    return <FailedToInitialize />
  }

  return (
    <Routes>
      {/* Sim Mode QA harness — kiosk hosts only (operator-authenticated). Never
          registered in digital mode, so the public web booth can't reach it. */}
      {!isDigitalMode() && <Route path="sim" element={<SimHost />} />}
      <Route
        path="/*"
        element={
          <SimFlowRecapture>
            <ViewProject />
          </SimFlowRecapture>
        }
      />
    </Routes>
  )
}

export default ProjectRoute
